import { Injectable, inject } from '@angular/core';
// import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface ConfigValue {
  key: string;
  value: string;
  createdAt: string;
  updatedAt: string;
}

export interface MaintenanceStatus {
  isActive: boolean;
  maintenanceText: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
// export class ConfigService implements OnDestroy {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/config`;
  private cache = new Map<string, string>();
  
  // Maintenance Status mit Caching + Periodic Refresh
  private maintenanceStatus$ = new BehaviorSubject<MaintenanceStatus>({
    isActive: false,
    maintenanceText: ''
  });
  // private refreshInterval: any;

  /**
   * Holt einen Config-Wert aus der Datenbank (mit Caching)
   */
  getConfig(key: string): Observable<string | null> {
    // Prüfe Cache
    if (this.cache.has(key)) {
      return of(this.cache.get(key)!);
    }

    // Lade von API
    return this.http.get<ConfigValue>(`${this.apiUrl}/${key}`).pipe(
      map(config => config.value),
      tap(value => {
        if (value) {
          this.cache.set(key, value);
        }
      }),
      catchError(error => {
        console.error(`Failed to load config key: ${key}`, error);
        return of(null);
      })
    );
  }

  constructor() {
    this.loadMaintenanceStatus();
    // this.startPeriodicRefresh();
  }

  /**
   * Maintenance Status abrufen (mit Caching)
   * [AllowAnonymous] auf Backend nötig - nicht-angemeldete User brauchen das!
   */
  getMaintenanceStatus(): Observable<MaintenanceStatus> {
    return this.maintenanceStatus$.asObservable();
  }

  /**
   * Maintenance Status direkt vom Backend laden (Fresh, ohne Cache)
   * Wird vom Guard verwendet um aktuellen Status zu prüfen
   */
  getMaintenanceStatusFresh(): Observable<MaintenanceStatus> {
    return this.http.get<MaintenanceStatus>(`${this.apiUrl}/maintenance-status`).pipe(
      catchError(error => {
        console.error('[MAINTENANCE_STATUS_FRESH] Error loading:', error);
        // Fallback: Maintenance OFF wenn API-Fehler
        return of({ isActive: false, maintenanceText: '' });
      })
    );
  }

  /**
   * Lädt Maintenance Status von Backend
   */
  private loadMaintenanceStatus(): void {
    this.http.get<MaintenanceStatus>(`${this.apiUrl}/maintenance-status`)
      .pipe(
        tap(status => {
          this.maintenanceStatus$.next(status);
          if (!environment.production) {
            console.log('[MAINTENANCE_STATUS] Loaded:', status);
          }
        })
      )
      .subscribe({
        error: (err) => {
          console.error('[MAINTENANCE_STATUS] Error loading:', err);
          // Fallback: Maintenance OFF wenn API-Fehler
          this.maintenanceStatus$.next({ isActive: false, maintenanceText: '' });
        }
      });
  }

  /**
   * Periodisch aktualisieren (30 Sekunden)
   * Schnelle Reaktion auf Admin-Änderungen
   * DEAKTIVIERT: Timer war nicht notwendig, da Guard sowieso bei jedem Route-Change fresh checkt
   */
  // private startPeriodicRefresh(): void {
  //   this.refreshInterval = setInterval(() => {
  //     this.loadMaintenanceStatus();
  //   }, 30000); // 30 Sekunden
  // }

  // ngOnDestroy(): void {
  //   if (this.refreshInterval) {
  //     clearInterval(this.refreshInterval);
  //   }
  // }

  /**
   * Holt den Stripe Publishable Key
   */
  getStripePublishableKey(): Observable<string | null> {
    return this.getConfig('Stripe:PublishableKey');
  }

  /**
   * Setzt einen Config-Wert im Cache (nur client-seitig)
   */
  setCachedConfig(key: string, value: string): void {
    this.cache.set(key, value);
  }

  /**
   * Löscht den Cache
   */
  clearCache(): void {
    this.cache.clear();
  }
}
